import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import { type AiFeedItem, AiFeedItemStatus } from "./types";

export interface RetrieveAiFeedResponse {
  feedItems: AiFeedItem[];
}

export const getPendingAiFeedItemCount = (
  feedItems: AiFeedItem[] | undefined,
) => {
  return (
    feedItems?.filter((item) => item.status === AiFeedItemStatus.pending)
      .length ?? 0
  );
};

/**
 * Fetches the AI Feed for an encounter from the charting service.
 * Fetches on mount and re-fetches whenever the query is invalidated
 * (e.g. after accept/reject actions).
 *
 * @param patientId - The patient id for cache scoping.
 * @param encounterId - The FHIR Encounter id to scope the feed to.
 * @param options.enabled - When false, the query does not fire. Defaults to true.
 */
export const useAiFeed = (
  patientId: string,
  encounterId?: string,
  options?: { enabled?: boolean },
) => {
  return useApi<RetrieveAiFeedResponse, AiFeedItem[]>({
    queryOptions: {
      queryFn: async () => {
        const response = await axiosInstance.get<RetrieveAiFeedResponse>(
          `/charting/ai-feed/encounter/${encounterId}/feed`,
        );
        return response.data;
      },
      select: (response) => response.feedItems,
      queryKey: QueryKeys.Patient.MedicalRecords.AiFeed.DEFAULT(
        patientId,
        encounterId,
      ),
      enabled: Boolean(encounterId) && (options?.enabled ?? true),
    },
  });
};
